import { Tab, Tabs } from "@mui/material";
import React, { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";

export type AuthIntent = "login" | "signup";
export type AuthMethod = "email" | "sms";

export interface AuthIntentTabsProps {
  intent: AuthIntent;
  /** Preserve email vs SMS when switching Sign In ↔ Sign Up */
  method?: AuthMethod;
  /** Modal / in-place switch instead of routing */
  onIntentChange?: (intent: AuthIntent) => void;
}

function withSearch(path: string, search: string): string {
  if (!search || search === "?") {
    return path;
  }
  const qs = search.startsWith("?") ? search.slice(1) : search;
  return qs ? `${path}?${qs}` : path;
}

export function authHref(
  intent: AuthIntent,
  method: AuthMethod,
  search = ""
): string {
  if (method === "sms") {
    const base = `/account/sms?intent=${intent}`;
    if (!search || search === "?") {
      return base;
    }
    const params = new URLSearchParams(
      search.startsWith("?") ? search.slice(1) : search
    );
    params.delete("intent");
    const rest = params.toString();
    return rest ? `${base}&${rest}` : base;
  }
  return withSearch(
    intent === "signup" ? "/account/signup" : "/account/login",
    search
  );
}

const AuthIntentTabs: React.FC<AuthIntentTabsProps> = ({
  intent,
  method = "email",
  onIntentChange,
}) => {
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();
  const searchSuffix = search ? `?${search}` : "";

  const loginTo = useMemo(
    () => authHref("login", method, searchSuffix),
    [method, searchSuffix]
  );
  const signupTo = useMemo(
    () => authHref("signup", method, searchSuffix),
    [method, searchSuffix]
  );

  return (
    <Tabs
      value={intent}
      variant="fullWidth"
      sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      data-attr="auth_intent-tabs"
      onChange={(_event, value: AuthIntent) => {
        if (onIntentChange && value !== intent) {
          onIntentChange(value);
        }
      }}
    >
      <Tab
        label="Sign In"
        value="login"
        {...(onIntentChange
          ? {}
          : {
              component: Link,
              to: loginTo,
            })}
        data-attr="auth_tab-sign-in"
      />
      <Tab
        label="Sign Up"
        value="signup"
        {...(onIntentChange
          ? {}
          : {
              component: Link,
              to: signupTo,
            })}
        data-attr="auth_tab-sign-up"
      />
    </Tabs>
  );
};

export default AuthIntentTabs;
